import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi, paymentApi, couponApi } from '../services/api';
import toast from 'react-hot-toast';
import { Brain, ArrowRight, Loader2, Check, Tag, Send, AlertTriangle } from 'lucide-react';

const STEPS_FULL = ['Welcome', 'About You', 'Payment'];
const STEPS_PROFILE_ONLY = ['About You'];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman & Nicobar Islands', 'Chandigarh', 'Dadra & Nagar Haveli and Daman & Diu',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const LANGUAGES = [
  { value: 'English', label: 'English' },
  { value: 'Hindi', label: 'Hindi' },
  { value: 'Kannada', label: 'Kannada' },
  { value: 'Tamil', label: 'Tamil' },
  { value: 'Telugu', label: 'Telugu' },
  { value: 'Malayalam', label: 'Malayalam' },
  { value: 'Marathi', label: 'Marathi' },
  { value: 'Gujarati', label: 'Gujarati' },
  { value: 'Bengali', label: 'Bengali' },
  { value: 'Punjabi', label: 'Punjabi' },
  { value: 'Odia', label: 'Odia' },
  { value: 'Urdu', label: 'Urdu' }
];

const EXAMS = [
  'NEET', 'JEE Main', 'JEE Advanced', 'UPSC', 'CA Foundation', 'CA Inter', 'CA Final',
  'Class 10 Board', 'Class 12 Board', 'GATE', 'CAT', 'CLAT', 'NDA',
  'SSC CGL', 'SSC CHSL', 'Bank PO', 'Bank Clerk', 'CUET', 'KVPY', 'KCET',
  'NTSE', 'Olympiad', 'State PSC', 'Railway', 'Defence', 'Other'
];

const EXAM_MONTHS = [
  { value: '1', label: '1 month' },
  { value: '2', label: '2 months' },
  { value: '3', label: '3 months' },
  { value: '4', label: '4 months' },
  { value: '5', label: '5 months' },
  { value: '6', label: '6 months' },
  { value: '9', label: '9 months' },
  { value: '12', label: '1 year' },
  { value: '18', label: '1.5 years' },
  { value: '24', label: '2 years' },
  { value: '24+', label: 'More than 2 years' },
  { value: 'not-sure', label: 'Not sure yet' }
];

const LEARNER_TYPES = [
  { value: 'Visual learner', label: 'Visual learner - I remember what I see' },
  { value: 'Auditory learner', label: 'Auditory learner - I remember what I hear' },
  { value: 'Reading/Writing learner', label: 'Reading/Writing - I learn by reading and notes' },
  { value: 'Kinesthetic learner', label: 'Kinesthetic - I learn by doing' },
  { value: 'Mixed learner', label: 'Mixed - a bit of everything' }
];

// Chat question definitions
const QUESTIONS = [
  {
    id: 'state',
    botMessage: (name) => `Hi ${name}! Let's personalize your experience. Which state are you from?`,
    type: 'select',
    options: INDIAN_STATES.map(s => ({ value: s, label: s })),
    placeholder: 'Select State / UT...',
    required: true
  },
  {
    id: 'city',
    botMessage: () => 'Which city or district?',
    type: 'text',
    placeholder: 'Type your city or district...',
    required: true
  },
  {
    id: 'area',
    botMessage: () => 'And your area or locality?',
    type: 'text',
    placeholder: 'Type your area (optional)...',
    required: false,
    skip: true
  },
  {
    id: 'languages',
    botMessage: () => 'What languages are you comfortable learning in?',
    type: 'chips',
    options: LANGUAGES,
    required: false
  },
  {
    id: 'age',
    botMessage: () => 'How old are you?',
    type: 'select',
    options: [
      ...Array.from({ length: 51 }, (_, i) => ({ value: String(i + 10), label: String(i + 10) })),
      { value: '60+', label: '60+' }
    ],
    placeholder: 'Select age...',
    required: true
  },
  {
    id: 'exam',
    botMessage: () => 'What exam are you preparing for?',
    type: 'select',
    options: EXAMS.map(e => ({ value: e, label: e })),
    placeholder: 'Select exam...',
    required: true
  },
  {
    id: 'exam_other',
    botMessage: () => 'Please tell me the name of your exam.',
    type: 'text',
    placeholder: 'Enter your exam name...',
    required: true,
    conditional: (answers) => answers.exam === 'Other'
  },
  {
    id: 'exam_months',
    botMessage: () => 'When is your exam?',
    type: 'select',
    options: EXAM_MONTHS,
    placeholder: 'Select timeline...',
    required: false,
    skip: true
  },
  {
    id: 'learner_type',
    botMessage: () => 'What kind of learner are you?',
    type: 'select',
    options: LEARNER_TYPES,
    placeholder: 'Select learner type...',
    required: false,
    skip: true
  },
  {
    id: 'struggles',
    botMessage: () => "What are you struggling with the most?",
    type: 'textarea',
    placeholder: 'e.g. I forget formulas easily, dates in history confuse me...',
    required: false,
    skip: true
  }
];

// Typing indicator component
function TypingIndicator() {
  return (
    <div className="flex items-center space-x-1 px-4 py-3">
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

function Onboarding() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [botTyping, setBotTyping] = useState(false);
  const [chatDone, setChatDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedChips, setSelectedChips] = useState([]);
  const chatEndRef = useRef(null);

  // Payment state
  const [feeData, setFeeData] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // If already completed onboarding, go to chat
  useEffect(() => {
    if (user?.onboardingCompleted) {
      navigate('/chat', { replace: true });
    }
  }, [user?.onboardingCompleted]);

  // If user already paid, skip welcome and go straight to profile questions
  useEffect(() => {
    if (user?.registrationPaid && step === 0) {
      setStep(1);
    }
  }, [user?.registrationPaid]);

  useEffect(() => {
    if (step === 2 && !feeData && !feeLoading) {
      loadFeeData();
    }
  }, [step]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, botTyping]);

  // When entering Step 1, start the chat with the first question
  useEffect(() => {
    if (step === 1 && chatMessages.length === 0) {
      askQuestion(0);
    }
  }, [step]);

  // Get the effective question list (skip conditionals that don't apply)
  const getNextQuestionIndex = (fromIndex) => {
    let next = fromIndex + 1;
    while (next < QUESTIONS.length) {
      const q = QUESTIONS[next];
      if (q.conditional && !q.conditional(answers)) {
        next++;
        continue;
      }
      return next;
    }
    return -1; // no more questions
  };

  const askQuestion = (qIndex) => {
    setBotTyping(true);
    setTimeout(() => {
      const q = QUESTIONS[qIndex];
      const msg = q.botMessage(user?.name || 'there');
      setChatMessages(prev => [...prev, { role: 'bot', content: msg }]);
      setBotTyping(false);
      setCurrentQ(qIndex);
      setInputValue('');
      setSelectedChips([]);
    }, 500);
  };

  const handleAnswer = (displayText, value) => {
    const q = QUESTIONS[currentQ];

    // Save user message bubble
    setChatMessages(prev => [...prev, { role: 'user', content: displayText }]);

    // Save answer
    const newAnswers = { ...answers, [q.id]: value };
    setAnswers(newAnswers);

    // Move to next question
    const nextIdx = getNextQuestionIndex(currentQ);
    if (nextIdx === -1) {
      // All done — show completion message
      setBotTyping(true);
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          role: 'bot',
          content: "Great, I've got everything I need! Let's set up your account."
        }]);
        setBotTyping(false);
        setChatDone(true);
      }, 500);
    } else {
      // Check if next question's conditional applies with new answers
      let actualNext = nextIdx;
      while (actualNext < QUESTIONS.length) {
        const nq = QUESTIONS[actualNext];
        if (nq.conditional && !nq.conditional(newAnswers)) {
          actualNext++;
          continue;
        }
        break;
      }
      if (actualNext >= QUESTIONS.length) {
        setBotTyping(true);
        setTimeout(() => {
          setChatMessages(prev => [...prev, {
            role: 'bot',
            content: "Great, I've got everything I need! Let's set up your account."
          }]);
          setBotTyping(false);
          setChatDone(true);
        }, 500);
      } else {
        askQuestion(actualNext);
      }
    }
  };

  const handleSkip = () => {
    const q = QUESTIONS[currentQ];
    setChatMessages(prev => [...prev, { role: 'user', content: 'Skipped' }]);

    const newAnswers = { ...answers };
    setAnswers(newAnswers);

    const nextIdx = getNextQuestionIndex(currentQ);
    if (nextIdx === -1) {
      setBotTyping(true);
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          role: 'bot',
          content: "Great, I've got everything I need! Let's set up your account."
        }]);
        setBotTyping(false);
        setChatDone(true);
      }, 500);
    } else {
      askQuestion(nextIdx);
    }
  };

  const handleSelectSubmit = (e) => {
    const val = e.target.value;
    if (!val) return;
    const q = QUESTIONS[currentQ];
    const opt = q.options.find(o => o.value === val);
    handleAnswer(opt?.label || val, val);
  };

  const handleTextSubmit = () => {
    if (!inputValue.trim()) return;
    handleAnswer(inputValue.trim(), inputValue.trim());
  };

  const handleChipsSubmit = () => {
    if (selectedChips.length === 0) {
      handleSkip();
      return;
    }
    const labels = selectedChips.map(v => {
      const q = QUESTIONS[currentQ];
      const opt = q.options.find(o => o.value === v);
      return opt?.label || v;
    });
    handleAnswer(labels.join(', '), selectedChips);
  };

  const handleTextareaSubmit = () => {
    if (!inputValue.trim()) {
      handleSkip();
      return;
    }
    handleAnswer(inputValue.trim(), inputValue.trim());
  };

  const handleContinueToPayment = async () => {
    setSaving(true);
    try {
      const examName = answers.exam === 'Other' ? answers.exam_other : answers.exam;
      await authApi.saveOnboardingProfile({
        name: user?.name || null,
        state: answers.state || null,
        city: answers.city || null,
        area: answers.area || null,
        languages: answers.languages || [],
        age: answers.age || null,
        exam: examName || null,
        exam_months: answers.exam_months || null,
        learner_type: answers.learner_type || null,
        struggles: answers.struggles || null
      });
      // Mark onboarding as complete after profile is saved
      await authApi.completeOnboarding();
    } catch (error) {
      console.error('Failed to save onboarding profile:', error);
    }
    setSaving(false);

    // Profile saved — go to chat
    useAuthStore.setState(state => ({
      user: { ...state.user, onboardingCompleted: true }
    }));
    navigate('/chat', { replace: true });
  };

  const toggleChip = (value) => {
    setSelectedChips(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  // Payment handlers
  const loadFeeData = async () => {
    setFeeLoading(true);
    try {
      const res = await paymentApi.getRegistrationFee();
      setFeeData(res.data);
    } catch (error) {
      toast.error('Failed to load fee details');
    } finally {
      setFeeLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !feeData) return;
    setCouponLoading(true);
    try {
      const res = await couponApi.validate(couponCode.trim(), feeData.total);
      setCouponDiscount(res.data);
      toast.success(`Coupon applied! You save ₹${res.data.discount}`);
    } catch (error) {
      setCouponDiscount(null);
      toast.error(error.response?.data?.error || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponDiscount(null);
  };

  const handlePayment = async () => {
    setPaymentProcessing(true);
    try {
      const orderRes = await paymentApi.createRegistrationOrder(
        couponDiscount ? couponCode.trim() : null
      );
      const { paymentUrl } = orderRes.data;
      window.location.href = paymentUrl;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
      setPaymentProcessing(false);
    }
  };

  const handleFreeTrial = async () => {
    try {
      await authApi.completeOnboarding();
      useAuthStore.setState(state => ({
        user: { ...state.user, onboardingCompleted: true }
      }));
      navigate('/chat', { replace: true });
    } catch (error) {
      toast.error('Failed to start free trial');
    }
  };

  // Render input area for current question
  const renderChatInput = () => {
    if (chatDone || botTyping) return null;

    const q = QUESTIONS[currentQ];
    if (!q) return null;

    switch (q.type) {
      case 'select':
        return (
          <div className="p-3 border-t border-gray-200 bg-white">
            <select
              value=""
              onChange={handleSelectSubmit}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            >
              <option value="">{q.placeholder || 'Select...'}</option>
              {q.options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {q.skip && (
              <button
                onClick={handleSkip}
                className="mt-2 text-sm text-gray-400 hover:text-gray-600"
              >
                Skip
              </button>
            )}
          </div>
        );

      case 'text':
        return (
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                placeholder={q.placeholder || 'Type here...'}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                autoFocus
              />
              <button
                onClick={handleTextSubmit}
                disabled={!inputValue.trim() && q.required}
                className="p-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            {q.skip && (
              <button
                onClick={handleSkip}
                className="mt-2 text-sm text-gray-400 hover:text-gray-600"
              >
                Skip
              </button>
            )}
          </div>
        );

      case 'chips':
        return (
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex flex-wrap gap-2 mb-3">
              {q.options.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleChip(opt.value)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedChips.includes(opt.value)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={handleChipsSubmit}
              className="w-full py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              {selectedChips.length > 0 ? 'Next' : 'Skip'}
            </button>
          </div>
        );

      case 'textarea':
        return (
          <div className="p-3 border-t border-gray-200 bg-white">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={q.placeholder || 'Type here...'}
              rows={2}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none mb-2"
              autoFocus
            />
            <button
              onClick={handleTextareaSubmit}
              className="w-full py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              {inputValue.trim() ? 'Next' : 'Skip'}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex flex-col">
      {/* Progress Bar — only show for full flow (not profile-only) */}
      {!user?.registrationPaid && (
        <div className="w-full px-4 pt-6 pb-2">
          <div className="max-w-lg mx-auto flex items-center justify-center space-x-3">
            {STEPS_FULL.map((label, i) => (
              <div key={i} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  i < step ? 'bg-green-500 text-white' :
                  i === step ? 'bg-primary-600 text-white ring-4 ring-primary-100' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                {i < STEPS_FULL.length - 1 && (
                  <div className={`w-12 h-0.5 mx-1 ${i < step ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-2 text-sm text-gray-500">{STEPS_FULL[step]}</div>
        </div>
      )}
      {user?.registrationPaid && (
        <div className="w-full px-4 pt-6 pb-2">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-800">Complete Your Profile</h2>
            <p className="text-sm text-gray-500 mt-1">Help us personalize your learning experience</p>
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="flex-1 flex items-start justify-center px-4 py-6 overflow-y-auto">
        <div className="w-full max-w-lg">

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Brain className="w-10 h-10 text-primary-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                Welcome to Memory Coach!
              </h1>
              <p className="text-gray-600 text-lg mb-2">
                Powered by <span className="font-semibold text-primary-700">Deepak Sir's</span> proven memory techniques
              </p>
              <p className="text-gray-500 mb-8">
                Turn any boring textbook content into unforgettable stories using the Peg System, Memory Palace, and more. Let's set you up in 2 minutes!
              </p>
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center px-8 py-3 bg-primary-600 text-white text-lg font-semibold rounded-xl hover:bg-primary-700 transition-colors"
              >
                Let's Get Started <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          )}

          {/* Step 1: Chat-style About You */}
          {step === 1 && (
            <div className="bg-white rounded-2xl shadow-lg flex flex-col" style={{ height: 'calc(100vh - 180px)', maxHeight: '600px' }}>
              {/* Chat header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <Brain className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Memory Coach</p>
                    <p className="text-xs text-green-500">Online</p>
                  </div>
                </div>
              </div>

              {/* Chat messages area */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-primary-600 text-white rounded-2xl rounded-br-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-2xl rounded-bl-md'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))}

                {botTyping && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-md">
                      <TypingIndicator />
                    </div>
                  </div>
                )}

                {chatDone && !botTyping && (
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={handleContinueToPayment}
                      disabled={saving}
                      className="inline-flex items-center px-6 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors"
                    >
                      {saving ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                      ) : (
                        <>{user?.registrationPaid ? 'Save Profile' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </button>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Chat input area */}
              {renderChatInput()}
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Complete Registration</h2>
              <p className="text-gray-500 mb-5">
                Unlock access to Deepak Sir's proven memory techniques
              </p>

              {feeLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
                  <p className="text-gray-600 font-medium">Loading fee details...</p>
                </div>
              ) : feeData ? (
                <>
                  {/* Fee Breakdown Card */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Fee Breakdown</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Course Fee</span>
                        <span className="text-gray-800">₹{feeData.courseFee?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Service Fee</span>
                        <span className="text-gray-800">₹{feeData.serviceFee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Platform Fee</span>
                        <span className="text-gray-800">₹{feeData.platformFee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">GST</span>
                        <span className="text-gray-800">₹{feeData.gst}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-gray-200 mt-1">
                        <span className="text-gray-600">Starter Pack ({feeData.freeQuestions} Questions)</span>
                        <span className="text-gray-800">₹{feeData.starterPackBase}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Starter Pack GST (18%)</span>
                        <span className="text-gray-800">₹{feeData.starterPackGst}</span>
                      </div>
                      {couponDiscount && (
                        <div className="flex justify-between text-green-600">
                          <span>Coupon Discount</span>
                          <span>-₹{couponDiscount.discount}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold pt-2 border-t border-gray-300 text-base">
                        <span>Total</span>
                        <span className="text-primary-700">₹{couponDiscount
                          ? couponDiscount.finalAmount.toLocaleString('en-IN')
                          : feeData.total.toLocaleString('en-IN')
                        }</span>
                      </div>
                    </div>
                  </div>

                  {/* What you get */}
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-primary-800 font-medium mb-1">What you get:</p>
                    <ul className="text-sm text-primary-700 space-y-1">
                      <li>• Personalized memory techniques by Deepak Sir</li>
                      <li>• AI-powered memory coaching assistant</li>
                      <li>• Access to all memory techniques & courses</li>
                      <li>• Peg System, Memory Palace & more</li>
                      <li>• <strong>{feeData.freeQuestions} AI questions</strong> included (Starter Pack)</li>
                    </ul>
                  </div>

                  {/* Coupon Section */}
                  <div className="mb-4">
                    {couponDiscount ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center">
                          <Tag className="w-4 h-4 text-green-600 mr-2" />
                          <div>
                            <p className="text-green-700 font-medium text-sm">
                              "{couponCode}" applied!
                            </p>
                            <p className="text-xs text-green-600">
                              You save ₹{couponDiscount.discount}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={removeCoupon}
                          className="text-sm text-red-500 hover:text-red-600 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            placeholder="Have a coupon code?"
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <button
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                        >
                          {couponLoading ? 'Checking...' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Pay Button */}
                  <button
                    onClick={() => { setShowPaymentModal(true); setAgreedToTerms(false); }}
                    disabled={paymentProcessing}
                    className="w-full py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                  >
                    {paymentProcessing ? (
                      <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</>
                    ) : (
                      <>Pay ₹{couponDiscount
                        ? couponDiscount.finalAmount.toLocaleString('en-IN')
                        : feeData.total.toLocaleString('en-IN')
                      } <ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </button>

                  <p className="text-center text-xs text-gray-400 mt-3">
                    Secure payment powered by Instamojo
                  </p>

                  {/* Divider */}
                  <div className="flex items-center my-4">
                    <div className="flex-1 border-t border-gray-200"></div>
                    <span className="px-3 text-xs text-gray-400">or</span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>

                  {/* Free Trial Button */}
                  <button
                    onClick={handleFreeTrial}
                    className="w-full py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors text-sm"
                  >
                    Start Free Trial Instead
                  </button>
                  <p className="text-center text-xs text-gray-400 mt-2">
                    Try Memory Coach with limited daily questions for 7 days
                  </p>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-red-500 mb-2">Failed to load fee details.</p>
                  <button
                    onClick={loadFeeData}
                    className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment Confirmation Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center mr-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Important</h3>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
              <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-2">
                <li>1. Please <strong>do not close the browser</strong> or press back during the payment process.</li>
                <li>2. After successful payment, you will be <strong>automatically redirected</strong> back to the website.</li>
                <li>3. Wait for the redirect to complete — <strong>do not navigate away</strong> until you see the confirmation.</li>
                <li>4. If payment gets stuck, contact support or the admin can recover it from the dashboard.</li>
              </ul>
            </div>
            <label className="flex items-start space-x-3 mb-5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                I have read the above instructions and agree to the{' '}
                <a href="/privacy-policy" target="_blank" className="text-primary-600 dark:text-primary-400 underline">Privacy Policy</a>
              </span>
            </label>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowPaymentModal(false); handlePayment(); }}
                disabled={!agreedToTerms}
                className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center"
              >
                Proceed to Pay <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Onboarding;
